import React, { Suspense } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { Box } from '@atlaskit/primitives/compiled/box';
import { ExternalAgentProfileCardSkeleton } from '@atlassian/agent-profile-card/external-card-skeleton';
import { AgentCardLoading } from '@atlassian/agent-profile-card/loading';

import { useFireRenderedSpinner } from '../../hooks/useProfileCardAnalytics';
import type { PersonProfileData } from '../../types';
import { getProfileCardKind, type ProfileCardKind } from '../getProfileCardKind';
import { profileQueryAccountId } from '../profileQueryAccountId';
import type { CardLoadingByIdentityQuery } from './__generated__/CardLoadingByIdentityQuery.graphql';
import { CardLoading } from './CardLoading';

const messages = defineMessages({
	loadingAgentProfile: {
		id: 'user-profile-card.CardLoadingByIdentity.loadingAgentProfile',
		defaultMessage: 'Loading agent profile',
		description: 'Accessible status while an agent profile card loads.',
	},
});

type CardLoadingByIdentityProps = {
	userId: string;
	testId?: string;
	profileData?: PersonProfileData;
	isReduced?: boolean;
	hasInvokedBy?: boolean;
};

function AgentLoading({
	kind,
	testId,
	hasInvokedBy,
	hasDescription,
}: {
	kind: Exclude<ProfileCardKind, 'person'>;
	testId?: string;
	hasInvokedBy?: boolean;
	hasDescription?: boolean;
}) {
	const { formatMessage } = useIntl();
	useFireRenderedSpinner();

	return (
		<Box role="status" aria-label={formatMessage(messages.loadingAgentProfile)}>
			{kind === 'external-agent' ? (
				<ExternalAgentProfileCardSkeleton
					testId={testId}
					attributionRows={hasInvokedBy ? 2 : 0}
					hasDescription={hasDescription}
				/>
			) : (
				<AgentCardLoading testId={testId} />
			)}
		</Box>
	);
}

function ResolvedIdentityLoading({
	userId,
	testId,
	profileData,
	isReduced,
	hasInvokedBy,
}: CardLoadingByIdentityProps): React.JSX.Element {
	const data = useLazyLoadQuery<CardLoadingByIdentityQuery>(
		graphql`
			query CardLoadingByIdentityQuery($accountId: ID!) {
				user(accountId: $accountId) {
					__typename
					... on AppUser {
						appType
						extendedProfile {
							jobTitle
							aboutMe
						}
					}
				}
			}
		`,
		{ accountId: profileQueryAccountId(userId) },
		{ fetchPolicy: 'store-or-network' },
	);
	const kind = getProfileCardKind({
		userType: data.user?.__typename,
		appType: data.user?.__typename === 'AppUser' ? data.user.appType : null,
		jobTitle: data.user?.__typename === 'AppUser' ? data.user.extendedProfile?.jobTitle : null,
	});

	if (kind === 'person') {
		return <CardLoading testId={testId} profileData={profileData} isReduced={isReduced} />;
	}

	return (
		<AgentLoading
			kind={kind}
			testId={testId}
			hasInvokedBy={hasInvokedBy}
			hasDescription={
				data.user?.__typename === 'AppUser' && Boolean(data.user.extendedProfile?.aboutMe)
			}
		/>
	);
}

/** Waits for the AppUser marker before choosing a card-shaped skeleton. */
export function CardLoadingByIdentity(props: CardLoadingByIdentityProps): React.JSX.Element {
	return (
		<ErrorBoundary fallback={null}>
			<Suspense fallback={null}>
				<ResolvedIdentityLoading {...props} />
			</Suspense>
		</ErrorBoundary>
	);
}
